import type { CmsArticle, CmsArticleStatus, CmsCategory, SiteStaffRole } from '@/lib/cms-types';
import { fixMojibake } from '@/lib/fix-mojibake';
import { apiDelete, apiGet, apiPost, apiPut } from './api-client';

type ListResponse<T> = { data: T };
type ApiGuide = Record<string, unknown>;

function normalizeGuide(raw: ApiGuide): CmsArticle {
  const category =
    raw.categoryId ??
    raw.category_id ??
    (raw.category as { slug?: string } | undefined)?.slug ??
    '';

  return {
    id: String(raw.id ?? ''),
    title: fixMojibake(String(raw.title ?? '')),
    categoryId: String(category),
    status: (raw.status as CmsArticleStatus) ?? 'draft',
    content: fixMojibake(String(raw.content ?? '')),
    createdAt: String(raw.createdAt ?? raw.created_at ?? new Date().toISOString()),
    updatedAt: String(raw.updatedAt ?? raw.updated_at ?? new Date().toISOString()),
    publishedAt:
      raw.publishedAt != null
        ? String(raw.publishedAt)
        : raw.published_at != null
          ? String(raw.published_at)
          : null,
    featured: Boolean(raw.featured),
    views: Number(raw.views ?? raw.views_count ?? 0) || 0,
    readingTime: Number(raw.readingTime ?? raw.reading_time ?? 0) || undefined,
    recentlyUpdated:
      raw.recentlyUpdated != null
        ? Boolean(raw.recentlyUpdated)
        : raw.recently_updated != null
          ? Boolean(raw.recently_updated)
          : undefined,
    sourceRef:
      raw.sourceRef != null
        ? String(raw.sourceRef)
        : raw.source_ref != null
          ? String(raw.source_ref)
          : undefined,
    slug: raw.slug != null ? String(raw.slug) : undefined,
    icon:
      raw.icon != null && String(raw.icon).trim()
        ? String(raw.icon).trim()
        : null,
    updatedById: raw.updatedById != null ? String(raw.updatedById) : undefined,
    updatedByName: raw.updatedByName != null ? String(raw.updatedByName) : undefined,
    updatedByHelpCenterRole:
      raw.updatedByHelpCenterRole != null
        ? (raw.updatedByHelpCenterRole as SiteStaffRole)
        : raw.updated_by_help_center_role != null
          ? (raw.updated_by_help_center_role as SiteStaffRole)
          : undefined,
    updatedByOfficialXatRole:
      raw.updatedByOfficialXatRole != null
        ? String(raw.updatedByOfficialXatRole)
        : raw.updated_by_official_xat_role != null
          ? String(raw.updated_by_official_xat_role)
          : undefined,
    updatedByAvatarUrl:
      raw.updatedByAvatarUrl != null
        ? String(raw.updatedByAvatarUrl)
        : raw.updated_by_avatar_url != null
          ? String(raw.updated_by_avatar_url)
          : undefined,
    deletedAt:
      raw.deletedAt != null
        ? String(raw.deletedAt)
        : raw.deleted_at != null
          ? String(raw.deleted_at)
          : undefined,
  };
}

function toApiGuidePayload(input: Partial<CmsArticle>): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  if (input.title !== undefined) payload.title = fixMojibake(input.title);
  if (input.slug !== undefined) payload.slug = input.slug;
  if (input.status !== undefined) payload.status = input.status;
  if (input.content !== undefined) payload.content = fixMojibake(input.content);
  if (input.categoryId !== undefined) payload.category_slug = input.categoryId;
  if (input.sourceRef !== undefined) payload.source_ref = input.sourceRef;
  if (input.featured !== undefined) payload.featured = input.featured;
  if (input.icon !== undefined) payload.icon = input.icon?.trim() || null;
  return payload;
}

export async function getCategories(): Promise<CmsCategory[]> {
  const res = await apiGet<ListResponse<CmsCategory[]>>('/guides/categories');
  return res.data ?? [];
}

export async function getPublicGuides(category?: string): Promise<CmsArticle[]> {
  const qs = category ? `?category=${encodeURIComponent(category)}` : '';
  const res = await apiGet<ListResponse<ApiGuide[]>>(`/guides${qs}`);
  return (res.data ?? []).map(normalizeGuide);
}

export async function getPublicGuide(category: string, slug: string): Promise<CmsArticle | null> {
  try {
    const res = await apiGet<ListResponse<ApiGuide>>(`/guides/${category}/${slug}`);
    return res.data ? normalizeGuide(res.data) : null;
  } catch {
    return null;
  }
}

export async function getAdminGuides(): Promise<CmsArticle[]> {
  const res = await apiGet<ListResponse<ApiGuide[]>>('/admin/guides', true);
  return (res.data ?? []).map(normalizeGuide);
}

export async function createGuide(input: Partial<CmsArticle>): Promise<CmsArticle> {
  const res = await apiPost<ListResponse<ApiGuide>>('/admin/guides', toApiGuidePayload(input), true);
  return normalizeGuide(res.data);
}

export async function updateGuide(id: string, patch: Partial<CmsArticle>): Promise<CmsArticle> {
  const res = await apiPut<ListResponse<ApiGuide>>(`/admin/guides/${id}`, toApiGuidePayload(patch), true);
  return normalizeGuide(res.data);
}

export async function deleteGuide(id: string): Promise<void> {
  await apiDelete(`/admin/guides/${id}`, true);
}

export async function getTrashedGuides(): Promise<CmsArticle[]> {
  try {
    const res = await apiGet<ListResponse<ApiGuide[]>>('/admin/guides/trash', true);
    return (res.data ?? []).map(normalizeGuide);
  } catch {
    return [];
  }
}

export async function restoreGuide(id: string): Promise<CmsArticle> {
  const res = await apiPost<ListResponse<ApiGuide>>(`/admin/guides/${id}/restore`, {}, true);
  return normalizeGuide(res.data);
}

export async function forceDeleteGuide(id: string): Promise<void> {
  await apiDelete(`/admin/guides/${id}/force`, true);
}

export async function createCategory(input: Omit<CmsCategory, 'id'>): Promise<CmsCategory> {
  const res = await apiPost<ListResponse<CmsCategory>>('/admin/guide-categories', input, true);
  return res.data;
}

export async function updateCategory(id: string, patch: Partial<CmsCategory>): Promise<CmsCategory> {
  const res = await apiPut<ListResponse<CmsCategory>>(`/admin/guide-categories/${id}`, patch, true);
  return res.data;
}

export async function deleteCategory(id: string): Promise<void> {
  await apiDelete(`/admin/guide-categories/${id}`, true);
}
